import { Link } from "react-router-dom";
import { Bot, CircleCheck, Plug, Zap } from "lucide-react";

import { HelpTip } from "@/components/common";
import { HeroCard, HeroPill, HeroWordmark, IconSquare, Panel, PanelTitle, ShareBar, StatGrid } from "@/components/panels";

// The Your Agent page's parts, built from the same shared pieces as the GrowthOS Alloy page (components/panels)
// so the two read as siblings. Each part only draws the facts it is handed (components/agent/agentFacts.js).

const ROW_BUTTON = "inline-flex h-9 shrink-0 items-center justify-center rounded-lg border border-sm-ink/15 bg-white px-3.5 text-xs font-bold text-sm-primary transition hover:border-sm-accent/60 hover:bg-sm-accent/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent";
const TEXT_LINK = "text-xs font-bold text-sm-primary hover:text-sm-ink";

export const slug = (text) => String(text).toLowerCase().replace(/[^a-z]+/g, "-");

// The Alloy page's three blues: lightest for what every agent has, deepest for what is the brand's own.
export const SOURCE_BARS = { "Stock Hermes": "bg-[#9cc6f0]", GrowthOS: "bg-[#4690D9]", "Brand-specific": "bg-[#003B8D]" };

const CARD_PILLS = { ok: { tone: "on", text: "READY", dot: true }, warn: { tone: "warn", text: "ATTENTION" }, bad: { tone: "bad", text: "ATTENTION" } };

// The card's headline is the agent's name, at the size its length allows: a first name reads like the Alloy
// card's balance, a long one steps down and may take two lines.
function nameSize(name) {
  const length = String(name || "").length;
  if (length <= 10) return "text-[44px]";
  if (length <= 18) return "text-[34px]";
  return "text-[26px]";
}

/** The agent's card: who it is, whether it is ready, and the workspace it belongs to. */
export function AgentCard({ about, skills, tone = "ok", className = "" }) {
  const pill = CARD_PILLS[tone] || CARD_PILLS.ok;
  return (
    <HeroCard grow className={className} data-testid="agent-profile-summary">
      <div className="flex items-center justify-between gap-3">
        <HeroWordmark product="Agent" />
        <HeroPill tone={pill.tone} dot={pill.dot} testId="agent-card-pill">{pill.text}</HeroPill>
      </div>
      <div>
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white/10 ring-1 ring-inset ring-white/20">
            {about.imageUrl
              ? <img src={about.imageUrl} alt={`${about.name} profile`} className="h-full w-full object-cover" />
              : <Bot size={26} className="text-[#9cc6f0]" aria-hidden="true" />}
          </div>
          <h2 data-testid="agent-name" className={`line-clamp-2 min-w-0 font-display font-extrabold leading-[1.05] tracking-[-0.02em] ${nameSize(about.name)}`}>{about.name}</h2>
        </div>
        {skills ? (
          <div data-testid="agent-card-line" className="mt-3 text-[13px] text-white/75">
            {`${skills.active} active ${skills.active === 1 ? "skill" : "skills"}`}
            {skills.brandSpecific > 0 ? ` · ${skills.brandSpecific} made for your brand` : ""}
          </div>
        ) : null}
      </div>
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0 truncate text-[13px] font-bold text-white/90">{about.workspace}</div>
        <div className="flex shrink-0 items-center gap-2.5">
          <div className="text-right">
            <div className="text-xs font-bold text-white/90">Workspace scoped</div>
            <div className="text-[11px] text-white/65">Only sees this workspace</div>
          </div>
          <HelpTip label="What does workspace scoped mean?" className="text-left">
            This agent only works inside {about.workspace}. It can read the sources connected to this workspace and nothing
            belonging to another brand, and every request is checked against your workspace access before it runs.
          </HelpTip>
        </div>
      </div>
    </HeroCard>
  );
}

const ISSUE_ICONS = { credits: Zap, connections: Plug };
const SOURCE_WORDS = { connections: "connections", credits: "credits" };

/**
 * The things the client can fix themselves, each with the way to fix it. `checking` names sources still loading,
 * `unknown` sources that could not be read, so an empty list never claims more than was looked at.
 */
export function IssuesPanel({ issues, checking = [], unknown = [], className = "" }) {
  return (
    <Panel className={className} data-testid="agent-issues" data-count={issues.length} aria-labelledby="agent-issues-title">
      <PanelTitle id="agent-issues-title" aside={issues.length ? <span className="text-xs tabular-nums text-sm-ink/65">{`${issues.length} to fix`}</span> : null}>
        Needs your attention
      </PanelTitle>
      {issues.length ? (
        <ul className="mt-2">
          {issues.map((issue) => (
            <li key={issue.key} data-testid={`agent-issue-${issue.key}`} className="grid grid-cols-[36px_minmax(0,1fr)] items-center gap-x-3.5 gap-y-2.5 border-t border-sm-ink/[0.08] py-3 first:border-t-0 sm:grid-cols-[36px_minmax(0,1fr)_auto]">
              <IconSquare icon={ISSUE_ICONS[issue.kind] || Plug} tone={issue.tone} />
              <div className="min-w-0">
                <div className="text-sm font-bold text-sm-ink">{issue.title}</div>
                <div className="text-xs leading-relaxed text-sm-ink/65">{issue.text}</div>
              </div>
              <Link to={issue.action.to} data-testid={`agent-issue-action-${issue.key}`} className={`${ROW_BUTTON} col-start-2 justify-self-start sm:col-start-3 sm:justify-self-end`}>{issue.action.label}</Link>
            </li>
          ))}
        </ul>
      ) : checking.length ? (
        <p data-testid="agent-issues-checking" className="mt-3 text-[13px] text-sm-ink/65">{`Checking your ${checking.map((source) => SOURCE_WORDS[source]).join(" and ")}…`}</p>
      ) : (
        <div data-testid="agent-issues-clear" className="mt-3 grid grid-cols-[36px_minmax(0,1fr)] items-center gap-x-3.5">
          <IconSquare icon={CircleCheck} tone="ok" size={17} />
          <div className="min-w-0">
            <div className="text-sm font-bold text-sm-ink">Nothing needs fixing</div>
            <div className="text-xs leading-relaxed text-sm-ink/65">Everything your agent needs is in place. Ask it for anything in Slack.</div>
          </div>
        </div>
      )}
      {unknown.length ? (
        <p data-testid="agent-issues-unknown" className="mt-3 border-t border-sm-ink/[0.08] pt-3 text-xs text-sm-ink/60">{`Your ${unknown.map((source) => SOURCE_WORDS[source]).join(" and ")} could not be checked just now.`}</p>
      ) : null}
    </Panel>
  );
}

const waiting = (status) => (status === "loading" ? { value: "…", sub: "Loading" } : { value: "—", sub: "Not available right now" });

// A figure only takes a colour when the client can see something is wrong with it: red for a connection that is
// not working or credits that have run out, amber for one not tested yet or credits about to.
const glanceTone = (tone) => (tone === "bad" || tone === "warn" ? tone : undefined);

function alloyTone(alloy) {
  if (!alloy || !alloy.configured) return undefined;
  if (alloy.out || alloy.rechargeFailed) return "bad";
  return alloy.low ? "warn" : undefined;
}

/** Connections, GrowthOS Alloy and the team in one row, each with the way to its own page. */
export function GlancePanel({ connections, connectionsStatus, alloy, alloyStatus, team, teamStatus, className = "" }) {
  const stats = [
    {
      label: "Connections",
      testId: "agent-connections-summary",
      tone: glanceTone(connections?.tone),
      ...(connections
        ? {
          value: connections.word,
          sub: connections.broken.length
            ? `${connections.broken.length} not working`
            : connections.unverified.length
              ? `${connections.unverified.length} not tested yet`
              : connections.total === 0 ? "Connect the tools your agent should use" : connections.checked || "Checked for real",
        }
        : waiting(connectionsStatus)),
      action: <Link to="/connections" data-testid="agent-link-connections" className={TEXT_LINK}>Manage connections</Link>,
    },
    {
      label: "GrowthOS Alloy",
      testId: "agent-alloy-summary",
      tone: alloyTone(alloy),
      ...(alloy
        ? !alloy.configured
          ? { value: "Not set up", sub: alloy.blocker }
          : alloy.on || alloy.pending
            ? { value: alloy.balance, sub: alloy.out ? "Out of tokens" : alloy.runway || "Prepaid Alloy tokens for your agent" }
            : { value: "Off", sub: alloy.balanceUsd > 0 ? `${alloy.balance}${alloy.tokens ? "" : " in credits"}, ready when you turn it on` : "Managed model access, paid for in Alloy tokens" }
        : waiting(alloyStatus)),
      action: <Link to="/alloy" data-testid="agent-link-alloy" className={TEXT_LINK}>Open Alloy</Link>,
    },
    {
      label: "Team",
      testId: "agent-team-summary",
      ...(team
        ? { value: `${team.total} ${team.total === 1 ? "person" : "people"}`, sub: team.roles.join(" · ") || "Nobody has access yet" }
        : waiting(teamStatus)),
      action: <Link to="/team" data-testid="agent-link-team" className={TEXT_LINK}>{team?.canManage ? "Manage team" : "See team"}</Link>,
    },
  ];
  return (
    <Panel className={className} data-testid="agent-glance" aria-labelledby="agent-glance-title">
      <PanelTitle id="agent-glance-title">At a glance</PanelTitle>
      <StatGrid stats={stats} className="mt-3" />
    </Panel>
  );
}

/**
 * What the agent can do: the counts, when a skill last changed, and where the skills come from. "See all" opens
 * the skills popup (components/agent/SkillsDialog.jsx), where the three groups live; the changes themselves are
 * the Skill activity log panel (components/agent/SkillUpdatesFeed.jsx).
 */
export function SkillsPanel({ skills, status, onOpen, className = "" }) {
  return (
    <Panel className={className} data-testid="agent-skills" aria-labelledby="agent-skills-title">
      <PanelTitle
        id="agent-skills-title"
        aside={skills?.active ? (
          <button type="button" data-testid="agent-skills-open" onClick={onOpen} className={`min-h-[32px] rounded-lg px-1.5 ${TEXT_LINK}`}>
            {`See all ${skills.active} skills`}
          </button>
        ) : null}
      >
        Skills
      </PanelTitle>

      {status === "loading" ? (
        <p data-testid="agent-skills-loading" className="mt-3 text-[13px] text-sm-ink/65">Loading skills…</p>
      ) : status === "error" ? (
        <p className="mt-3 text-[13px] leading-relaxed text-sm-ink/70">Skills could not be loaded. No placeholder capabilities have been substituted. Refresh this page to retry the skill registry.</p>
      ) : (
        <>
          <StatGrid
            data-testid="agent-skill-summary"
            className="mt-3"
            stats={[
              { label: "Active skills", value: skills.active, testId: "agent-skill-total" },
              { label: "Customized", value: skills.brandSpecific },
              { label: "Last update", value: skills.lastUpdate ?? "—", testId: "agent-skill-last-update" },
            ]}
          />
          {skills.split.length ? (
            <ShareBar
              testId="agent-skill-sources"
              parts={skills.split.map((part) => ({ key: part.key, percent: part.percent, barClass: SOURCE_BARS[part.key], legend: `${part.count} ${part.key}` }))}
            />
          ) : null}
          {skills.lastUsed || skills.mostUsed.length ? (
            <p data-testid="agent-skill-usage" className="mt-3 text-xs leading-relaxed text-sm-ink/65">
              {[skills.lastUsed, skills.mostUsed.length ? `Most used: ${skills.mostUsed.join(", ")}` : null].filter(Boolean).join(" · ")}
            </p>
          ) : null}
          {skills.active === 0 ? (
            <p className="mt-3 text-xs leading-relaxed text-sm-ink/65">The agent skill registry is connected, but it did not report any active skills for this workspace.</p>
          ) : null}
        </>
      )}
    </Panel>
  );
}
